import React, { useState } from 'react';
import axios from 'axios';
import { MapPin, X, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';

// Use relative URL for same-origin API calls
const API = '/api';

const LocationOnboarding = ({ isOpen, onClose, userId, onLocationSet }) => {
  const [location, setLocation] = useState('');
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!location.trim()) {
      toast.error('Please enter your wedding location');
      return;
    }

    setLoading(true);
    try {
      // Save to wedding_plans
      await axios.put(`${API}/wedding-plans/${userId}`, {
        venue_location: location.trim()
      });
      
      toast.success('Wedding location saved!');
      if (onLocationSet) onLocationSet();
      onClose(true, false); // completed, not skipped
    } catch (error) {
      console.error('Error saving location:', error);
      toast.error('Failed to save location');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    onClose(false, true); // not completed, skipped
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#881337] to-[#9F1239] px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                <MapPin className="text-white" size={28} />
              </div>
              <div>
                <h2 className="text-2xl font-serif font-light text-white">Wedding Location</h2>
                <p className="text-rose-100 text-sm">Where will you celebrate?</p>
              </div>
            </div>
            <button
              onClick={() => onClose(false, false)}
              className="text-white/70 hover:text-white transition-colors"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-8">
          <div className="mb-6">
            <p className="text-[#57534E] mb-4">
              Enter the city and state where you're planning your wedding. This helps us find local vendors and services for you.
            </p>
            <p className="text-sm text-[#78716C] mb-6 flex items-center gap-2">
              <MapPin size={14} />
              We'll search within 50 miles of this location
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#1C1917] mb-2">
                Wedding Location (City, State)
              </label>
              <Input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="e.g., Austin, TX or San Francisco, CA"
                className="rounded-xl h-12 text-lg"
                autoFocus
              />
            </div>

            <div className="bg-rose-50 rounded-xl p-4 border border-rose-100">
              <p className="text-sm text-[#881337]">
                <strong>Tip:</strong> Even if you're not 100% sure of your exact venue, enter the general area. You can always update this later in your wedding details.
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-between items-center mt-8 pt-6 border-t border-stone-100">
            <Button
              type="button"
              variant="ghost"
              onClick={handleSkip}
              className="text-[#57534E] hover:text-[#881337]"
            >
              Skip for now
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="rounded-full bg-gradient-to-r from-[#881337] to-[#9F1239] hover:from-[#9F1239] hover:to-[#881337] text-white px-8"
            >
              {loading ? 'Saving...' : 'Save Location'}
              <ArrowRight size={18} className="ml-2" />
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LocationOnboarding;
